import { ReactNode } from "react";

import { HasErrors } from "@/components/types";
import { Label } from "@/components/ui/Label";
import { cn } from "@/lib/cn";

import { Combobox } from "./Combobox";
import { SelectOption } from "./types";

export type SelectProps<OptionValue extends string> = HasErrors & {
  id: string;
  /** Focus the control when it is mounted */
  autoFocus?: boolean;
  /** Sets a className attribute on the  component wrapper */
  className?: string;
  /** Id of a hint under the field, read out with it */
  describedBy?: string;
  /** Is the select value clearable. Shows clear indicator when true */
  isClearable?: boolean;
  /** Is the select disabled */
  isDisabled?: boolean;
  /** Is the select in loading state (Async) */
  isLoading?: boolean;
  /** Label copy for the select (a11y) */
  label: string;
  /** Should the label be visible in the UI */
  labelVisible?: boolean;
  /** Element to show when no options visible in dropdown */
  noOptionsMessage?: (obj: { inputValue: string }) => ReactNode;
  /** Handle change events on the select */
  onChange: (option: SelectOption<OptionValue> | null) => void;
  /** Array of options that populate the select menu */
  options: SelectOption<OptionValue>[];
  /** Custom placeholder copy */
  placeholder?: string;
  /** Is the select required. Renders an asterisk near label if set tu true */
  required?: boolean;
  /** "sm" is the 32px control of a toolbar */
  size?: "default" | "sm";
  value?: SelectOption<OptionValue> | null;
};

export function Select(props: SelectProps<string>) {
  const {
    id,
    className,
    label,
    labelVisible,
    noOptionsMessage = () => "Nothing to select...",
    placeholder = "Select item...",
    required,
    onChange,
    value,
    ...other
  } = props;

  return (
    <div
      className={cn("relative min-w-[min(300px,100%)] text-sm font-semibold", className)}
      data-testid="select-wrapper"
    >
      <Label htmlFor={id} variant="field" className={cn(labelVisible === false && "sr-only")}>
        {label}
        {required && " *"}
      </Label>
      <Combobox
        {...other}
        id={id}
        label={label}
        placeholder={placeholder}
        noOptionsMessage={noOptionsMessage}
        value={value ? [value] : []}
        onChange={(selected) => onChange(selected[0] ?? null)}
      />
    </div>
  );
}
